import React from "react";
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Alert,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { Engineering as EngineeringIcon } from "@mui/icons-material";
import { red } from "@mui/material/colors";

const SystemConfigSettings = ({ settings, onSettingChange }) => {
  const systemConfig = settings?.systemConfig || {};

  const handleInputChange = (key, value, type = "int") => {
    let processedValue = value;

    if (type === "int") {
      processedValue = parseInt(value) || 0;
    } else if (type === "boolean") {
      processedValue = Boolean(value);
    }

    console.log(`Updating system config ${key} to:`, processedValue);
    onSettingChange("systemConfig", key, processedValue);
  };

  const numberSettings = [
    {
      key: "max_concurrent_rooms",
      label: "Max Concurrent Rooms",
      helperText: "Maximum number of rooms that can run simultaneously",
    },
    {
      key: "max_room_participants",
      label: "Max Room Participants",
      helperText: "Maximum number of participants per room",
    },
    {
      key: "default_room_duration",
      label: "Default Room Duration (minutes)",
      helperText: "Default duration for new rooms in minutes",
    },
    {
      key: "api_rate_limit",
      label: "API Rate Limit",
      helperText: "Maximum API requests per hour per user",
    },
    {
      key: "file_upload_max_size",
      label: "File Upload Max Size (MB)",
      helperText: "Maximum file upload size in megabytes",
    },
  ];

  const booleanSettings = [
    {
      key: "auto_cleanup_inactive_rooms",
      label: "Auto Cleanup Inactive Rooms",
      description: "Automatically remove inactive rooms after a period of time",
    },
    {
      key: "email_notifications_enabled",
      label: "Email Notifications",
      description: "Enable email notifications for users",
    },
    {
      key: "push_notifications_enabled",
      label: "Push Notifications",
      description: "Enable push notifications for mobile apps",
    },
    {
      key: "rate_limiting_enabled",
      label: "Rate Limiting",
      description: "Enable API rate limiting protection",
    },
    {
      key: "image_upload_enabled",
      label: "Image Upload",
      description: "Allow users to upload images",
    },
    {
      key: "video_upload_enabled",
      label: "Video Upload",
      description: "Allow users to upload videos",
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ mb: 3 }}>
          This section requires backend implementation for System Configuration.
          API endpoints not yet available.
        </Alert>

        <Card elevation={3}>
          <CardHeader
            avatar={<EngineeringIcon sx={{ color: red[500] }} />}
            title="System Configuration"
            subheader="Configure system limits and features"
            sx={{ bgcolor: red[50] }}
          />
          <CardContent>
            <Grid container spacing={3}>
              {/* Numeric Settings Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: red[700] }}>
                  System Limits & Performance
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              {numberSettings.map((setting) => (
                <Grid item xs={12} sm={6} key={setting.key}>
                  <TextField
                    fullWidth
                    label={setting.label}
                    type="number"
                    value={systemConfig[setting.key] || ""}
                    onChange={(e) =>
                      handleInputChange(setting.key, e.target.value, "int")
                    }
                    helperText={setting.helperText}
                    disabled
                    variant="outlined"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        step: setting.key === "default_room_duration" ? 5 : 1,
                      },
                    }}
                  />
                </Grid>
              ))}

              {/* Boolean Settings Section */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: red[700] }}>
                  Feature Toggles
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={3}>
                  {booleanSettings.map((setting) => (
                    <Grid item xs={12} sm={6} md={4} key={setting.key}>
                      <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={systemConfig[setting.key] || false}
                              onChange={(e) =>
                                handleInputChange(
                                  setting.key,
                                  e.target.checked,
                                  "boolean"
                                )
                              }
                              disabled
                              color={
                                setting.key.includes("notifications")
                                  ? "primary"
                                  : setting.key.includes("upload")
                                  ? "secondary"
                                  : setting.key.includes("cleanup")
                                  ? "warning"
                                  : "default"
                              }
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="subtitle2" component="div">
                                {setting.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {setting.description}
                              </Typography>
                            </Box>
                          }
                          labelPlacement="end"
                          sx={{
                            alignItems: "flex-start",
                            margin: 0,
                            width: "100%",
                          }}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Configuration Summary */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: red[700] }}>
                  Current Configuration Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Card variant="outlined" sx={{ bgcolor: red[50], p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Room Capacity:</strong>{" "}
                        {systemConfig.max_concurrent_rooms || 0} concurrent,
                        {systemConfig.max_room_participants || 0} participants
                        each
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Default Duration:</strong>{" "}
                        {systemConfig.default_room_duration || 0} minutes
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>API Rate Limit:</strong>{" "}
                        {systemConfig.api_rate_limit || 0} requests/hour
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Max File Size:</strong>{" "}
                        {systemConfig.file_upload_max_size || 0} MB
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Active Features:</strong>{" "}
                        {booleanSettings
                          .filter((setting) => systemConfig[setting.key])
                          .map((setting) => setting.label)
                          .join(", ") || "None"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SystemConfigSettings;
